import { useCallback, useEffect, useState } from 'react';
import type { FC } from 'react';

import type { Logger } from '@datadog/browser-logs';

import { getPlayer } from '../../player/playerService';
import { isAssetPaywalled } from '../../player/playerAssetHelpers';
import { usePlayerContext } from '../../player/PlayerContext';
import { AccountSharingPopup } from '../AccountSharingPopup/AccountSharingPopup';
import { usePlayerEvent } from '../utils/usePlayerEvent';
import { config } from '../../config';
import { getDatadog } from '../../services/datadog';

import { useASDClient } from './useASDClient';

let loggerPromise: Promise<Logger>;
const getLogger = async () => {
  if (loggerPromise) {
    return loggerPromise;
  }

  loggerPromise = getDatadog().then((dd) => dd.createLogger('ASD'));

  return loggerPromise;
};

export const AccountSharing: FC = () => {
  const [isCurrentAssetPaywalled, setIsCurrentAssetPaywalled] = useState(false);
  const [isAccountSharingDetected, setIsAccountSharingDetected] =
    useState(false);

  const { isPlaying } = usePlayerContext();

  const asdClientCallback = useCallback(async () => {
    setIsAccountSharingDetected(true);
    getLogger()
      .then((logger) => logger.info('Account sharing detected'))
      .catch(() => {});

    const player = await getPlayer();

    try {
      await player.stop();
    } catch {}

    // calling .remove() will make sure that it's not possible to resume playback e.g. from media notifications or picture-in-picture
    player.remove();
  }, []);

  const { asdClient } = useASDClient(asdClientCallback);

  useEffect(() => {
    if (!asdClient) {
      return;
    }

    if (isCurrentAssetPaywalled && isPlaying) {
      asdClient.start();
    }

    return () => asdClient.stop();
  }, [asdClient, isCurrentAssetPaywalled, isPlaying]);

  const handlePlay = useCallback(async () => {
    const player = await getPlayer();

    const asset = player.getRawAsset()!;

    setIsCurrentAssetPaywalled(isAssetPaywalled(asset));
  }, []);

  usePlayerEvent('play', handlePlay);

  return (
    <AccountSharingPopup
      variant={config.provider === 'vgtv' ? 'vg' : 'ab'}
      isOpen={isAccountSharingDetected}
    />
  );
};
